/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useState } from 'react';
import Typography from 'core/ui/Typography';
import Box from 'core/ui/Box';
import SFDataTable from 'core/ui/DataTable';
import LoaderSm from 'core/ui/LoaderSm';
import { useDebounce } from 'use-debounce';
import uuid from 'react-uuid';
import Modules from 'modules';
import useActions from 'modules/map/useActions';
import { showAPIError } from 'core/utils/showAPIError';
import useSelectors from 'modules/map/useSelectors';
import { ownerableMessagesModule } from 'modules/ownerableMessages';
import Checkbox from 'core/ui/Checkbox';
import { useTranslation } from 'react-i18next';
import TextFieldRounded from 'core/ui/TextFieldRounded';
import Paper from 'core/ui/Paper';

const TableContacts = ({ textService }) => {
  const { t } = useTranslation();
  const { request } = useActions();
  const [textSearch, setTextSearch] = useState(false);
  const [checkAll, setCheckAll] = useState(false);
  const [contacts, setContacts] = useState(null);
  const [updateTable, setUpdateTable] = useState(null);
  const loading = useSelectors(ownerableMessagesModule, 'loadingFormContacts');
  const [filterBounce] = useDebounce(updateTable, 500);

  const refreshTable = () => {
    if (!textService) {
      return;
    }
    const data = textService.getValues();

    request({
      action: Modules.ownerableMessages.actions.formMessageContacts,
      data: {
        ...data,
        ownerableType: textService?.ownerableType,
        ownerableId: textService?.ownerableId,

        customContacts: data.customContacts && data.customContacts.map((u) => u.id),
        competitionsUsers: data.competitionsUsers && data.competitionsUsers.map((u) => u?.id || u),
        membersTags: data.membersTags && data.membersTags.map((u) => u.name || u),
        membershipsTags: data.membershipsTags && data.membershipsTags.map((u) => u.name || u),
      },
      options: {
        onSuccess: (resp) => {
          setContacts(resp);
          if (!textService?.editing) {
            textService.setValue(
              'checkedUsers',
              resp?.map((x) => x.id),
            );
          }
        },
        onError: showAPIError,
      },
    });
  };

  useEffect(() => {
    if (filterBounce) {
      refreshTable();
    }
  }, [filterBounce]);

  useEffect(() => {
    const fieldsValidate = [
      'customContacts',
      'membersTypes',
      'membersTags',
      'membershipsTags',
      'competitionsCategories',
      'competitionsDivisions',
      'competitionsUsers',
      'competitionsWaitlist',
    ];

    const isValid = fieldsValidate?.find((x) => textService.watch(x)?.length > 0);
    const search = textService.watch('competitionId') || isValid;

    if (search) {
      setUpdateTable(uuid());
    } else {
      setContacts([]);
      textService.setValue('checkedUsers', []);
    }
  }, [
    textService.watch('competitionId'),
    textService.watch('membersTypes'),
    textService.watch('membersTags'),
    textService.watch('membershipsTags'),
    textService.watch('customContacts'),
    textService.watch('competitionsCategories'),
    textService.watch('competitionsDivisions'),
    textService.watch('competitionsUsers'),
    textService.watch('competitionsWaitlist'),
  ]);

  const checkUnCheckUser = useCallback((user) => {
    const arr = textService?.watch('checkedUsers') || [];

    if (isUserChecked(user)) {
      textService.setValue(
        'checkedUsers',
        arr.filter((x) => x !== user?.id),
      );
    } else {
      textService.setValue('checkedUsers', [...arr, user?.id]);
    }
  }, []);

  const isUserChecked = useCallback((user) => {
    const arr = textService?.watch('checkedUsers') || [];

    return !!arr.find((x) => x === user?.id);
  }, []);

  const checkUnCheckAll = useCallback(() => {
    if (checkAll) {
      textService.setValue('checkedUsers', []);
    } else {
      textService.setValue(
        'checkedUsers',
        contacts.map((x) => x.id),
      );
    }
    setCheckAll(!checkAll);
  }, [checkAll, contacts]);

  const columns = [
    {
      name: (
        <div>
          <Checkbox checked={checkAll} onClick={() => checkUnCheckAll()} />
        </div>
      ),
      selector: 'checked',
      width: '60px',
      cell: (user) => (
        <div>
          <Checkbox checked={!!isUserChecked(user)} onClick={() => checkUnCheckUser(user)} />
        </div>
      ),
    },
    {
      name: 'Member',
      selector: (row) => row?.name,
      sortable: true,
      cell: (user) => <Typography className="tw-font-semibold">{user?.name}</Typography>,
    },
  ];

  return (
    <Box className="tw-mt-2">
      <div className="tw-my-2 tw-flex tw-flex-row tw-items-end tw-justify-end tw-gap-2">
        <div>
          <Paper className="tw-p-4">
            <div className="tw-flex tw-flex-row tw-items-center tw-gap-2">
              <Typography className="tw-text-nowrap tw-font-semibold">
                {t('message.form.totalFiltered')}
              </Typography>
              <Typography className="text-font-bold tw-text-gray-600">
                {contacts?.length || 0}
              </Typography>
            </div>
            <div className="tw-flex tw-flex-row tw-items-center tw-gap-2">
              <Typography className="tw-text-nowrap tw-font-semibold">
                {t('message.form.totalCheckedUsers')}
              </Typography>
              <Typography className="text-font-bold tw-text-gray-600">
                {textService?.watch('checkedUsers')?.length || 0}
              </Typography>
            </div>
          </Paper>
        </div>
        <div className="tw-grow" />
        <TextFieldRounded
          bordered
          value={textSearch}
          onChange={(v) => setTextSearch(v)}
          placeholder={t('label.search')}
        />
      </div>

      <SFDataTable
        columns={columns}
        data={(contacts || []).filter(
          (x) => !textSearch || x?.name?.toLowerCase()?.indexOf(textSearch) > -1,
        )}
        pagination
        showPaper={false}
        noTableHead={false}
        paginationPerPage={20}
        paginationRowsPerPageOptions={[20, 50, 100]}
        noHeader
      />

      <LoaderSm center loading={loading} />
    </Box>
  );
};

TableContacts.propTypes = {};

TableContacts.defaultProps = {};

export default TableContacts;
